import React, { useCallback } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { FleetPage, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { RecyclePagedList } from '../../../../shared/recycle/recycle-paged-list'
import { ColumnFormat } from '../../../../shared/table-builder/table-columns'
import { recycleErrorMessage } from '../../../../shared/error/server-message'
import { agoText, utcText } from '../../../../shared/date/local-time'
import { RoleSysAiToolsTelemetry } from '../../../../role/components/role-sys'
import { NoAccess } from '../../../../role/components/no-access'
import { useRoleSysAiToolsTelemetry } from '../../../../role/hook/role-sys'
import { aiToolsAvailable, emptyRecycle, listRecycle, recycleWrite } from './ai-tools-api'
import { eventRow, findingRow, isTooMany, personNameOrgCell, postmortemRow, sessionRow, severityTone, tooManyText } from './ai-tools-words'
import { AiToolsTrail, recycleCrumb } from './ai-tools-trail'
import './ai-tools.scss'

// The AI tools telemetry recycle bin (R2, owner 2026-10-29): what staff
// moved there from the lists and pages, by kind in ?tab= (Sessions, Records,
// Postmortems, Findings, like the main page's tabs). Each tab is the shared
// paged recycle list (shared/recycle/recycle-paged-list.js) over ai's
// GET /admin/recycle?kind=, newest deleted first, searched (q): Restore and
// Permanently delete (typed "delete"), one row or in select mode, and Empty
// bin (typed "delete all") for that kind. The bin and everything in it need
// SYS_AI_TOOLS_TELEMETRY delete. A binned session or postmortem opens its
// page, which says it is in the recycle bin. Only rows binned on their own
// are here: a session's records, postmortems and findings went with it and
// come back with it.

// Grid cells (module-level, so AG Grid keeps its cells).
const sessionCell = cell => (cell.data
  ? <span className="ak-name-cell"><span className="fw-semibold text-truncate sw-mono">{cell.data.sessionText}</span><span className="sw-cell-sub text-truncate">{cell.data.sourceText}</span></span>
  : '')
const recordCell = cell => (cell.data ? <span className="sw-mono">{cell.data.kindText}</span> : '')
const toolCell = cell => (cell.data ? <span className="sw-mono">{cell.data.toolText}</span> : '')
const whenCell = cell => (cell.data ? cell.data.whenText : '')
// a postmortem's Written as the list reads it: how long ago, exact on hover
const writtenCell = cell => (cell.data && cell.data.ts ? <span title={`${cell.data.whenText} · ${utcText(cell.data.ts)}`}>{agoText(cell.data.ts)}</span> : '')
const titleCell = cell => (cell.data
  ? <span className="ak-name-cell"><span className="fw-semibold text-truncate">{cell.data.titleText}</span><span className="sw-cell-sub text-truncate">{cell.data.whatText || cell.data.sessionText}</span></span>
  : '')
const severityCell = cell => (cell.data && cell.data.severityText ? <Pill tone={severityTone(cell.data.severityText)}>{cell.data.severityText}</Pill> : '')

// The bin's tabs: the server's kind, what a row is, its columns and the page
// it opens. Purged counts come back by kind (DELETE /admin/recycle).
export const BIN_TABS = [
  {
    key: 'sessions', label: 'Sessions', kind: 'session', noun: 'session', items: 'sessions', purged: 'sessions', toRow: sessionRow,
    labelOf: row => `session ${row.sessionText}`, rowTarget: row => `/admin/ai-tools/sessions/${row.id}`,
    columns: [
      <ColumnFormat key="s" field="sessionText" header="Session" sortable={false} flex={1.4} stacked render={sessionCell} />,
      <ColumnFormat key="p" field="personText" header="Person" sortable={false} textMax={200} stacked render={personNameOrgCell} />
    ]
  },
  {
    key: 'records', label: 'Records', kind: 'event', noun: 'record', items: 'records', purged: 'events', toRow: eventRow,
    labelOf: row => `the ${row.kindText} record of ${row.whenText}`, rowTarget: row => (row.sessionId ? `/admin/ai-tools/sessions/${row.sessionId}` : null),
    columns: [
      <ColumnFormat key="w" field="whenText" header="When" sortable={false} fit="content" render={whenCell} />,
      <ColumnFormat key="k" field="kindText" header="Kind" sortable={false} fit="content" render={recordCell} />,
      <ColumnFormat key="t" field="toolText" header="Tool" sortable={false} render={toolCell} />
    ]
  },
  {
    key: 'postmortems', label: 'Postmortems', kind: 'postmortem', noun: 'postmortem', items: 'postmortems', purged: 'postmortems', toRow: postmortemRow,
    labelOf: row => `postmortem "${row.titleText}"`, rowTarget: row => `/admin/ai-tools/postmortems/${row.id}`,
    columns: [
      <ColumnFormat key="t" field="titleText" header="Postmortem" sortable={false} flex={1.6} textMax={220} stacked render={titleCell} />,
      <ColumnFormat key="p" field="personText" header="Person" sortable={false} textMax={200} stacked render={personNameOrgCell} />,
      <ColumnFormat key="w" field="whenText" header="Written" sortable={false} fit="content" render={writtenCell} />
    ]
  },
  {
    key: 'findings', label: 'Findings', kind: 'finding', noun: 'finding', items: 'findings', purged: 'findings', toRow: findingRow,
    labelOf: row => `finding "${row.titleText}"`, rowTarget: row => (row.postmortemId ? `/admin/ai-tools/postmortems/${row.postmortemId}` : null),
    columns: [
      <ColumnFormat key="t" field="titleText" header="Finding" sortable={false} flex={2} textMax={200} stacked render={titleCell} />,
      <ColumnFormat key="s" field="severityText" header="Severity" sortable={false} fit="content" render={severityCell} />,
      <ColumnFormat key="p" field="personText" header="Person" sortable={false} textMax={200} stacked render={personNameOrgCell} />
    ]
  }
]

// A bin selection's filter is its search alone, and "all matching" is
// bounded by when it was chosen (upTo: deleted_at).
const filterOf = ({ search, since }) => ({ ...(search ? { q: search } : {}), ...(since ? { upTo: since } : {}) })

const BinTab = ({ tab, canWrite }) => {
  const fetchPage = useCallback(params => listRecycle({ ...params, kind: tab.kind }), [tab.kind])
  const restore = useCallback(selection => recycleWrite('restore', tab.kind, selection), [tab.kind])
  const purge = useCallback(selection => recycleWrite('purge', tab.kind, selection), [tab.kind])
  const empty = useCallback(async () => {
    const answer = await emptyRecycle(tab.kind)
    const purged = answer && answer.purged
    return purged && Number.isFinite(purged[tab.purged]) ? purged[tab.purged] : undefined
  }, [tab.kind, tab.purged])
  const errorText = useCallback((err, fallback) => (isTooMany(err) ? tooManyText(tab.kind) : recycleErrorMessage(err, fallback)), [tab.kind])

  return (
    <RecyclePagedList tableSettings={false} title={tab.label} queryKey={`recycle-${tab.key}`} noun={tab.noun} items={tab.items} canWrite={canWrite}
      fetchPage={fetchPage} toRow={tab.toRow} filterOf={filterOf} restore={restore} purge={purge} empty={empty} errorText={errorText}
      labelOf={tab.labelOf} rowTarget={tab.rowTarget} failedText={`The recycle bin's ${tab.items} couldn't be loaded.`}>
      {tab.columns}
    </RecyclePagedList>
  )
}

const RecycleTabs = () => {
  const { search } = useLocation()
  const asked = new URLSearchParams(search).get('tab')
  const tab = BIN_TABS.find(t => t.key === asked) || BIN_TABS[0]
  const role = useRoleSysAiToolsTelemetry()

  return (
    <FleetPage title="Recycle bin" purpose="AI tools telemetry that staff deleted. Restore what you need, or delete it permanently." testId="ai-tools-recycle-page">
      <AiToolsTrail crumbs={[recycleCrumb(), { url: `/admin/ai-tools/recycle?tab=${tab.key}`, text: tab.label }]} />
      <nav className="sw-tabs" aria-label="Recycle bin">
        {BIN_TABS.map(t =>
          <NavLink key={t.key} to={`/admin/ai-tools/recycle?tab=${t.key}`} isActive={() => t.key === tab.key} activeClassName="active"
            aria-current={t.key === tab.key ? 'page' : undefined}>{t.label}</NavLink>)}
      </nav>
      {aiToolsAvailable()
        ? <BinTab key={tab.key} tab={tab} canWrite={!!role.accessDelete} />
        : <Notice tone="warn">AI tools telemetry isn't available in this build.</Notice>}
    </FleetPage>
  )
}

export const AiToolsRecyclePage = () =>
  <RoleSysAiToolsTelemetry accessDelete fallback={<NoAccess what="the AI tools telemetry recycle bin" />}>
    <RecycleTabs />
  </RoleSysAiToolsTelemetry>
